import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Button, FormControl,
  InputLabel, Select, MenuItem, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Tooltip, useMediaQuery, useTheme
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import api from '../services/api';

function CompanyLeaveCalendar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [calendar, setCalendar] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showInfoDialog, setShowInfoDialog] = useState(false);

  useEffect(() => {
    fetchCalendar();
  }, [selectedYear, selectedMonth]);

  const fetchCalendar = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/leave-calendar/${selectedYear}/${selectedMonth}`);
      setCalendar(response.data.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };

  const handleDayClick = (dayIndex) => {
    if (!calendar) return;
    
    const newCalendar = [...calendar.leaveCalendar];
    const currentValue = newCalendar[dayIndex];
    
    // Cycle through: null -> WO -> H -> PH -> null
    if (currentValue === null) {
      newCalendar[dayIndex] = 'WO';
    } else if (currentValue === 'WO') {
      newCalendar[dayIndex] = 'H';
    } else if (currentValue === 'H') {
      newCalendar[dayIndex] = 'PH';
    } else {
      newCalendar[dayIndex] = null;
    }
    
    setCalendar({
      ...calendar,
      leaveCalendar: newCalendar
    });
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      await api.put(`/leave-calendar/${selectedYear}/${selectedMonth}`, {
        leaveCalendar: calendar.leaveCalendar
      });
      setSuccess('Calendar saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
      fetchCalendar(); // Refresh to get updated counts
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save calendar');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset calendar to default (all Sundays as weekly offs)?')) return;
    
    try {
      setLoading(true);
      await api.post(`/leave-calendar/${selectedYear}/${selectedMonth}/reset`);
      setSuccess('Calendar reset to default!');
      setTimeout(() => setSuccess(''), 3000);
      fetchCalendar();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset calendar');
    } finally {
      setLoading(false);
    }
  };

  const getDayLabel = (value) => {
    if (value === null) return { label: 'W', color: 'success', text: 'Working' };
    if (value === 'WO') return { label: 'WO', color: 'default', text: 'Weekly Off' };
    if (value === 'H') return { label: 'H', color: 'warning', text: 'Holiday' };
    if (value === 'PH') return { label: 'PH', color: 'error', text: 'Public Holiday' };
    return { label: '-', color: 'default', text: 'Invalid' };
  };

  const getDaysInMonth = () => {
    return new Date(selectedYear, selectedMonth, 0).getDate();
  };

  const getDayOfWeek = (day) => {
    const date = new Date(selectedYear, selectedMonth - 1, day);
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 2; i <= currentYear + 2; i++) {
      years.push(i);
    }
    return years;
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const daysInMonth = getDaysInMonth();

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <CalendarIcon sx={{ fontSize: 40 }} />
          Company Leave Calendar
        </Typography>
        <IconButton onClick={() => setShowInfoDialog(true)} color="primary">
          <InfoIcon />
        </IconButton>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Month/Year Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Year</InputLabel>
                <Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  label="Year"
                >
                  {generateYearOptions().map(year => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Month</InputLabel>
                <Select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  label="Month"
                >
                  {months.map((month, index) => (
                    <MenuItem key={index + 1} value={index + 1}>{month}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleReset}
                  disabled={loading}
                  fullWidth={isMobile}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={loading || !calendar}
                  fullWidth={isMobile}
                >
                  Save
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Statistics */}
      {calendar && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Card sx={{ bgcolor: 'success.light', color: 'white' }}>
              <CardContent>
                <Typography variant="h4" fontWeight={700}>{calendar.totalWorkingDays}</Typography>
                <Typography variant="body2">Working Days</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ bgcolor: 'grey.400', color: 'white' }}>
              <CardContent>
                <Typography variant="h4" fontWeight={700}>{calendar.totalWeeklyOffs}</Typography>
                <Typography variant="body2">Weekly Offs</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ bgcolor: 'warning.main', color: 'white' }}>
              <CardContent>
                <Typography variant="h4" fontWeight={700}>{calendar.totalHolidays}</Typography>
                <Typography variant="body2">Holidays</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
              <CardContent>
                <Typography variant="h4" fontWeight={700}>{daysInMonth}</Typography>
                <Typography variant="body2">Total Days</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Calendar Grid */}
      {calendar && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {months[selectedMonth - 1]} {selectedYear} - Click to toggle day type
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell align="center"><strong>Day</strong></TableCell>
                    <TableCell align="center"><strong>Date</strong></TableCell>
                    <TableCell align="center"><strong>Type</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                    const dayIndex = day - 1;
                    const value = calendar.leaveCalendar[dayIndex];
                    const dayInfo = getDayLabel(value);
                    const dayOfWeek = getDayOfWeek(day);
                    const isSunday = dayOfWeek === 'Sun';
                    
                    return (
                      <TableRow 
                        key={day}
                        hover
                        onClick={() => handleDayClick(dayIndex)}
                        sx={{ 
                          cursor: 'pointer',
                          bgcolor: isSunday ? 'grey.50' : 'inherit',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <TableCell align="center">
                          <Typography sx={{ fontWeight: isSunday ? 700 : 400 }}>
                            {dayOfWeek}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography sx={{ fontWeight: isSunday ? 700 : 400 }}>
                            {day}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={dayInfo.text} arrow>
                            <Chip 
                              label={dayInfo.label}
                              color={dayInfo.color}
                              size="small"
                              sx={{ minWidth: 60, fontWeight: 600 }}
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Info Dialog */}
      <Dialog open={showInfoDialog} onClose={() => setShowInfoDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>How to Use Company Leave Calendar</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            The Company Leave Calendar is used to define working days and holidays for the entire organization.
          </Typography>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Day Types:
          </Typography>
          <Box sx={{ ml: 2, mb: 2 }}>
            <Typography variant="body2">• <Chip label="W" color="success" size="small" /> - Working Day</Typography>
            <Typography variant="body2">• <Chip label="WO" color="default" size="small" /> - Weekly Off (Sunday by default)</Typography>
            <Typography variant="body2">• <Chip label="H" color="warning" size="small" /> - Holiday</Typography>
            <Typography variant="body2">• <Chip label="PH" color="error" size="small" /> - Public Holiday</Typography>
          </Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            How to Edit:
          </Typography>
          <Typography variant="body2" paragraph>
            1. Select the desired month and year<br/>
            2. Click on any day to cycle through day types<br/>
            3. Click "Save" to update the calendar<br/>
            4. Use "Reset" to restore all Sundays as weekly offs
          </Typography>
          <Alert severity="info">
            This calendar affects salary calculations and is used to determine the number of working days in a month.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInfoDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CompanyLeaveCalendar;